import { IDataField, TFrequencyType, IEstimatedFileInfo } from "../types/reportsTypes";
import { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';

export const getFirstDayOfLastMonth = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 11 : now.getMonth() - 1; // Go to previous month

    // Create date string in YYYY-MM-DD format for the 1st of last month
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const firstDay = new Date(dateString);

    return firstDay;
};

export const getLastDayOfLastMonth = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 11 : now.getMonth() - 1; // Go to previous month

    // Get last day of the month by going to first day of next month and subtracting 1 day
    const lastDay = new Date(year, month + 1, 0);

    return lastDay;
};

export const getLastDayOfGivenMonth = (date: Date | string): Date => {
    const year = new Date(date).getFullYear();
    const month = new Date(date).getMonth();
    const lastDay = new Date(year, month + 1, 0); // 0 means last day of previous month
    return lastDay;
};

export const getFirstDayOfGivenMonth = (date: Date | string): Date => {
    const year = new Date(date).getFullYear();
    const month = new Date(date).getMonth();
    const firstDay = new Date(year, month, 1);// 1 means first day of the month
    return firstDay;
};

export const calculateDataExportEstimation = (
    vesselCount: number,
    dataFields: IDataField[],
    dateRange: RangeValue<DateValue>,
    frequency: TFrequencyType,
    fileType: string,
): IEstimatedFileInfo => {
    // Calculate time intervals between start and end date based on frequency
    const startTime = dateRange.start?.toDate('UTC').getTime() || 0;

    const endDate = dateRange.end?.toDate('UTC') || new Date();
    endDate.setUTCHours(23, 59, 59, 999);
    const endTime = endDate.getTime() || 0;

    const totalHours = Math.ceil((endTime - startTime) / (1000 * 60 * 60)); // Convert milliseconds to hours
    // Calculate number of records based on frequency
    let totalIntervals: number;
    switch (frequency) {
        case 'hourly':
            totalIntervals = totalHours;
            break;
        case 'daily':
            totalIntervals = Math.ceil(totalHours / 24);
            break;
        case 'monthly':
            // Approximate: 30.44 days per month on average
            totalIntervals = Math.ceil(totalHours / (24 * 30.44));
            break;
        case 'latest':
            totalIntervals = 1;
            break;
        default:
            totalIntervals = totalHours; // Default to hourly
    }

    // Calculate location-based vessel count modifier using density ratios
    let adjustedVesselCount = vesselCount;

    // Calculate total records: adjusted vessel count * frequency intervals
    const totalRecords = Math.ceil(adjustedVesselCount * totalIntervals);

    // Calculate bytes per record using actual columnSizeCsv values for selected fields
    const selectedFields = dataFields.filter((field) => field.selected);
    const bytesPerRecordCsv = selectedFields.reduce((total, field) => {
        return total + (field.columnSizeCsv || 0);
    }, 0);

    // Add CSV delimiter overhead (comma + space between fields, newline at end)
    const csvDelimiterOverhead = selectedFields.length > 0 ? (selectedFields.length - 1) * 2 + 1 : 0; // 2 bytes per delimiter (", ") + 1 byte for newline
    const totalBytesPerRecordCsv = bytesPerRecordCsv + csvDelimiterOverhead;

    // Calculate CSV file size
    const csvTotalBytes = totalRecords * totalBytesPerRecordCsv;

    // Calculate other file types based on CSV size with compression/overhead ratios
    let totalBytesWithOverhead: number;
    switch (fileType) {
        case 'csv':
            totalBytesWithOverhead = csvTotalBytes;
            break;
        case 'xlsx':
            // Excel files are typically 15-25% smaller than CSV due to binary storage and compression
            totalBytesWithOverhead = csvTotalBytes * 0.8;
            break;
        case 'parquet':
            // Parquet files are typically 70-85% smaller than CSV due to columnar storage and compression
            totalBytesWithOverhead = csvTotalBytes * 0.25;
            break;
        default:
            totalBytesWithOverhead = csvTotalBytes;
    }

    // Convert to appropriate unit
    let estimatedSize: string;
    if (totalBytesWithOverhead < 1024) {
        estimatedSize = `${totalBytesWithOverhead.toFixed(2)} B`;
    } else if (totalBytesWithOverhead < 1024 * 1024) {
        estimatedSize = `${(totalBytesWithOverhead / 1024).toFixed(2)} KB`;
    } else if (totalBytesWithOverhead < 1024 * 1024 * 1024) {
        estimatedSize = `${(totalBytesWithOverhead / (1024 * 1024)).toFixed(2)} MB`;
    } else {
        estimatedSize = `${(totalBytesWithOverhead / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }

    return {
        estimatedSize,
        estimatedRecords: totalRecords,
    };
};